import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  query,
  where,
  serverTimestamp,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, secondaryAuth } from "../services/firebase";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { PAYMENT_STATUS } from "../services/paymentService";
import { deleteUser } from "../services/userService";
import ActivatePackageByEmailModal from "../components/ActivatePackageByEmailModal";

// Bilingual Label Component
const BilingualLabel = ({ english, marathi, required = false, className = "" }) => (
  <label className={`block text-sm font-semibold text-gray-700 ${className}`}>
    {english} <span className="text-gray-500">/ {marathi}</span>
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const SuperAdminDashboard = ({ mode = "superadmin" }) => {
  const navigate = useNavigate();
  const isTeamView = mode === "team";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    companies: 0,
    jobs: 0,
    applications: 0,
    packages: 0,
    subscriptions: 0,
    revenue: 0,
    activeJobs: 0,
    pendingCompanies: 0,
    recentApplications: 0,
    monthlyRevenue: 0,
    teamMembers: 0,
    franchises: 0,
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [topCompanies, setTopCompanies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showAgentListModal, setShowAgentListModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showTeamListModal, setShowTeamListModal] = useState(false);
  const [teamForm, setTeamForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    password: "Team@1234",
  });
  const [teamFormError, setTeamFormError] = useState("");
  const [teamFormLoading, setTeamFormLoading] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [showTeamCredentialsModal, setShowTeamCredentialsModal] =
    useState(false);
  const [newTeamCredentials, setNewTeamCredentials] = useState(null);
  const [agentForm, setAgentForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    password: "Agent@123",
    commissionTiers: [{ minAmount: 0, maxAmount: 5000, percentage: 10 }],
  });
  const [agentFormError, setAgentFormError] = useState("");
  const [agentFormLoading, setAgentFormLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [newAgentCredentials, setNewAgentCredentials] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [showActivateEmailModal, setShowActivateEmailModal] = useState(false);

  // Payment filter states
  const [paymentFilterRange, setPaymentFilterRange] = useState('all');
  const [paymentFilterUserType, setPaymentFilterUserType] = useState('all');
  const [paymentFilterCustomStart, setPaymentFilterCustomStart] = useState('');
  const [paymentFilterCustomEnd, setPaymentFilterCustomEnd] = useState('');
  const [paymentSearchQuery, setPaymentSearchQuery] = useState('');

  const handleViewPaymentDetails = async (e) => {
    if (e) e.preventDefault();
    try {
      // Fetch users for enrichment
      const usersSnap = await getDocs(collection(db, "users"));
      const usersMap = {};
      usersSnap.docs.forEach((doc) => {
        const data = doc.data();
        usersMap[doc.id] = data;
        if (data.email) {
          usersMap[data.email.toLowerCase()] = data;
        }
      });

      // Fetch payments
      const q = query(
        collection(db, "payments"),
        where("status", "==", PAYMENT_STATUS.SUCCESS)
      );
      const snapshot = await getDocs(q);

      // Fetch active subscriptions to fallback/include any subscription missing in payments
      const subsSnap = await getDocs(collection(db, "subscriptions"));
      const existingPaymentIds = new Set(
        snapshot.docs.map((d) => d.id).concat(snapshot.docs.map((d) => d.data().paymentId).filter(Boolean))
      );

      // Enrich payments with user data
      const payments = snapshot.docs
        .map((doc) => {
          const paymentData = doc.data();
          const userData = usersMap[paymentData.userId] || (paymentData.userEmail ? usersMap[paymentData.userEmail.toLowerCase()] : null);
          const userName = userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || userData.displayName
            : paymentData.userName || "Unknown";
          const userEmail = userData?.email || paymentData.userEmail || "N/A";
          const userPhone = userData?.phone || paymentData.userPhone || "N/A";
          const userType = userData?.userType || paymentData.userType || "unknown";

          return {
            id: doc.id,
            ...paymentData,
            userName,
            userEmail,
            userPhone,
            userType,
            date:
              paymentData.createdAt?.toDate?.() ||
              new Date(paymentData.createdAt || Date.now()),
          };
        });

      subsSnap.docs.forEach((doc) => {
        const subData = doc.data();
        if (
          subData.price > 0 &&
          !existingPaymentIds.has(doc.id) &&
          !existingPaymentIds.has(subData.paymentId)
        ) {
          const userData = usersMap[subData.userId] || (subData.userEmail ? usersMap[subData.userEmail.toLowerCase()] : null);
          const subDate =
            subData.createdAt?.toDate?.() ||
            subData.startDate?.toDate?.() ||
            new Date(subData.createdAt || Date.now());
          const userName = userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || userData.displayName
            : subData.userName || "Unknown";
          const userEmail = userData?.email || subData.userEmail || "N/A";
          const userPhone = userData?.phone || subData.userPhone || "N/A";
          const userType = userData?.userType || subData.userType || "jobseeker";

          payments.push({
            id: doc.id,
            userId: subData.userId,
            packageName: subData.packageName || "Student Plan",
            amount: subData.price,
            totalAmount: subData.price,
            status: "success",
            userEmail,
            userName,
            userPhone,
            userType,
            createdAt: subData.createdAt || subData.startDate,
            date: subDate,
          });
        }
      });

      payments.sort((a, b) => b.date - a.date);

      setPaymentDetails(payments);
      // Reset filters when opening
      setPaymentFilterRange('all');
      setPaymentFilterUserType('all');
      setPaymentFilterCustomStart('');
      setPaymentFilterCustomEnd('');
      setPaymentSearchQuery('');
      setShowPaymentDetails(true);
    } catch (error) {
      console.error("Error fetching payment details:", error);
      alert("Error loading payment details. Please try again.");
    }
  };

  // Compute filtered payments
  const getFilteredPayments = () => {
    let filtered = [...paymentDetails];
    const now = new Date();

    // Date range filter
    if (paymentFilterRange === 'today') {
      filtered = filtered.filter(p => {
        const d = p.date;
        return d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate();
      });
    } else if (paymentFilterRange === 'last7') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 7);
      filtered = filtered.filter(p => p.date >= cutoff);
    } else if (paymentFilterRange === 'lastMonth') {
      const cutoff = new Date(now);
      cutoff.setMonth(cutoff.getMonth() - 1);
      filtered = filtered.filter(p => p.date >= cutoff);
    } else if (paymentFilterRange === 'custom') {
      if (paymentFilterCustomStart) {
        const start = new Date(paymentFilterCustomStart);
        start.setHours(0, 0, 0, 0);
        filtered = filtered.filter(p => p.date >= start);
      }
      if (paymentFilterCustomEnd) {
        const end = new Date(paymentFilterCustomEnd);
        end.setHours(23, 59, 59, 999);
        filtered = filtered.filter(p => p.date <= end);
      }
    }

    // User type filter
    if (paymentFilterUserType !== 'all') {
      filtered = filtered.filter(p => p.userType === paymentFilterUserType);
    }

    // Search filter
    if (paymentSearchQuery.trim()) {
      const q = paymentSearchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        (p.userName || '').toLowerCase().includes(q) ||
        (p.userEmail || '').toLowerCase().includes(q) ||
        (p.userPhone || '').toLowerCase().includes(q) ||
        (p.packageName || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  };

  const exportToCSV = () => {
    try {
      const filtered = getFilteredPayments();
      // Prepare CSV headers
      const headers = ["User Name", "Email", "Mobile No", "User Type", "Amount (₹)", "Package Name", "Date"];

      // Prepare CSV rows from filtered data
      const rows = filtered.map(payment => [
        payment.userName || "Unknown",
        payment.userEmail || "No email",
        payment.userPhone || "N/A",
        payment.userType === "jobseeker" ? "Job Seeker" : payment.userType === "employer" ? "Company" : "Unknown",
        payment.totalAmount || 0,
        payment.packageName || "Unknown",
        payment.date?.toLocaleDateString() || "Unknown"
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const rangeLabel = paymentFilterRange === 'all' ? 'all' : paymentFilterRange;
      link.setAttribute("download", `payment_details_${rangeLabel}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  const exportToExcel = () => {
    try {
      const filtered = getFilteredPayments();
      // Prepare table HTML for Excel
      const tableHTML = `
        <table>
          <thead>
            <tr>
              <th>User Name</th>
              <th>Email</th>
              <th>Mobile No</th>
              <th>User Type</th>
              <th>Amount (₹)</th>
              <th>Package Name</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${filtered.map(payment => `
              <tr>
                <td>${payment.userName || "Unknown"}</td>
                <td>${payment.userEmail || "No email"}</td>
                <td>${payment.userPhone || "N/A"}</td>
                <td>${payment.userType === "jobseeker" ? "Job Seeker" : payment.userType === "employer" ? "Company" : "Unknown"}</td>
                <td>${payment.totalAmount || 0}</td>
                <td>${payment.packageName || "Unknown"}</td>
                <td>${payment.date?.toLocaleDateString() || "Unknown"}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      `;

      // Create blob and download
      const blob = new Blob([tableHTML], { type: "application/vnd.ms-excel" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const rangeLabel = paymentFilterRange === 'all' ? 'all' : paymentFilterRange;
      link.setAttribute("download", `payment_details_${rangeLabel}_${new Date().toISOString().split('T')[0]}.xls`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export Excel. Please try again.");
    }
  };

  useEffect(() => {
    const safeFetchDocs = async (queryOrCol) => {
      try {
        const snap = await getDocs(queryOrCol);
        return snap;
      } catch (err) {
        console.log("ℹ️ [SuperAdminDashboard] Fallback for query:", err.message);
        return { docs: [], size: 0 };
      }
    };

    const fetchStats = async () => {
      try {
        const [
          usersSnap,
          companiesSnap,
          jobsSnap,
          packagesSnap,
          subscriptionsSnap,
          agentsSnap,
          applicationsSnap,
          franchisesSnap,
          teamSnap,
        ] = await Promise.all([
          safeFetchDocs(collection(db, "users")),
          safeFetchDocs(collection(db, "companies")),
          safeFetchDocs(collection(db, "jobs")),
          safeFetchDocs(collection(db, "packages")),
          safeFetchDocs(collection(db, "subscriptions")),
          safeFetchDocs(
            query(collection(db, "users"), where("userType", "==", "agent"))
          ),
          safeFetchDocs(collection(db, "applications")),
          safeFetchDocs(collection(db, "franchises")),
          safeFetchDocs(
            query(collection(db, "users"), where("userType", "==", "team"))
          ),
        ]);

        // Process job data
        const jobDocs = (jobsSnap.docs || []).map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const activeJobs = jobDocs.filter(
          (job) => job.status === "active"
        ).length;

        // Calculate applications count (combine applications collection size & job applicationCount)
        const totalJobAppCounts = jobDocs.reduce(
          (sum, job) => sum + (job.applicationCount || 0),
          0
        );
        const totalApplications = Math.max(applicationsSnap.size || 0, totalJobAppCounts);

        // Process company data
        const companyDocs = (companiesSnap.docs || []).map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const pendingCompanies = companyDocs.filter(
          (company) =>
            company.verificationStatus === "pending" ||
            !company.verificationStatus
        ).length;

        // Calculate jobs per company for top companies
        const companyJobCounts = {};
        jobDocs.forEach((job) => {
          if (job.companyId) {
            companyJobCounts[job.companyId] =
              (companyJobCounts[job.companyId] || 0) + 1;
          }
        });

        const topCompaniesList = companyDocs
          .map((company) => ({
            ...company,
            jobCount: companyJobCounts[company.id] || 0,
          }))
          .sort((a, b) => b.jobCount - a.jobCount)
          .slice(0, 5);

        // Create a map of users for quick lookup
        const usersMap = {};
        (usersSnap.docs || []).forEach((doc) => {
          const data = doc.data();
          usersMap[doc.id] = data;
          if (data.email) {
            usersMap[data.email.toLowerCase()] = data;
          }
        });

        // Calculate Franchises Count (from franchises collection and userType === 'franchise')
        const franchiseUserDocs = (usersSnap.docs || []).filter(
          (doc) => doc.data()?.userType === "franchise"
        );
        const totalFranchises = Math.max(franchisesSnap.size || 0, franchiseUserDocs.length);

        // Fetch payments for revenue calculation and recent purchases
        const paymentsSnap = await safeFetchDocs(
          query(
            collection(db, "payments"),
            where("status", "==", PAYMENT_STATUS.SUCCESS)
          )
        );

        const existingPaymentIds = new Set(
          (paymentsSnap.docs || []).map((d) => d.id).concat((paymentsSnap.docs || []).map((d) => d.data().paymentId).filter(Boolean))
        );

        // Enrich payments with user data
        const paymentDocs = (paymentsSnap.docs || []).map((doc) => {
          const paymentData = doc.data();
          const userData = usersMap[paymentData.userId] || (paymentData.userEmail ? usersMap[paymentData.userEmail.toLowerCase()] : null);
          const userName = userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || userData.displayName
            : paymentData.userName || "Unknown";
          const userEmail = userData?.email || paymentData.userEmail || "N/A";
          const userPhone = userData?.phone || paymentData.userPhone || "N/A";
          const userType = userData?.userType || paymentData.userType || "unknown";

          return {
            id: doc.id,
            ...paymentData,
            userName,
            userEmail,
            userPhone,
            userType,
            date:
              paymentData.createdAt?.toDate?.() ||
              new Date(paymentData.createdAt || Date.now()),
          };
        });

        // Merge active paid subscriptions missing in payments collection into revenue calculation
        (subscriptionsSnap.docs || []).forEach((doc) => {
          const subData = doc.data();
          if (
            subData.price > 0 &&
            !existingPaymentIds.has(doc.id) &&
            !existingPaymentIds.has(subData.paymentId)
          ) {
            const userData = usersMap[subData.userId] || (subData.userEmail ? usersMap[subData.userEmail.toLowerCase()] : null);
            const subDate =
              subData.createdAt?.toDate?.() ||
              subData.startDate?.toDate?.() ||
              new Date(subData.createdAt || Date.now());
            const userName = userData
              ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || userData.displayName
              : subData.userName || "Unknown";
            const userEmail = userData?.email || subData.userEmail || "N/A";
            const userPhone = userData?.phone || subData.userPhone || "N/A";
            const userType = userData?.userType || subData.userType || "jobseeker";

            paymentDocs.push({
              id: doc.id,
              userId: subData.userId,
              packageName: subData.packageName || "Student Plan",
              amount: subData.price,
              totalAmount: subData.price,
              status: "success",
              userEmail,
              userName,
              userPhone,
              userType,
              createdAt: subData.createdAt || subData.startDate,
              date: subDate,
            });
          }
        });

        // Calculate total revenue from payments
        const totalRevenue = paymentDocs.reduce(
          (sum, payment) => sum + (payment.totalAmount || payment.amount || 0),
          0
        );

        // Calculate monthly revenue (current month)
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const monthlyRevenue = paymentDocs
          .filter((payment) => {
            const paymentDate = payment.date;
            return (
              paymentDate.getMonth() === currentMonth &&
              paymentDate.getFullYear() === currentYear
            );
          })
          .reduce(
            (sum, payment) =>
              sum + (payment.totalAmount || payment.amount || 0),
            0
          );

        // Get recent jobs (last 5)
        const recentJobsList = jobDocs
          .sort(
            (a, b) =>
              new Date(b.createdAt?.toDate?.() || b.createdAt || 0) -
              new Date(a.createdAt?.toDate?.() || a.createdAt || 0)
          )
          .slice(0, 5);

        // Process team members list
        const teamList = (teamSnap.docs || []).map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTeamMembers(teamList);

        setStats({
          users: usersSnap.size || 0,
          companies: companiesSnap.size || 0,
          jobs: jobsSnap.size || 0,
          applications: totalApplications,
          packages: packagesSnap.size || 0,
          subscriptions: subscriptionsSnap.size || 0,
          revenue: totalRevenue,
          activeJobs,
          pendingCompanies,
          recentApplications: totalApplications,
          monthlyRevenue,
          teamMembers: teamList.length,
          franchises: totalFranchises,
        });

        // Process agents with referral counts safely
        const agentDocs = agentsSnap.docs || [];
        const agentsList = await Promise.all(
          agentDocs.map(async (doc) => {
            const agentData = { id: doc.id, ...doc.data() };
            try {
              const referralsSnap = await safeFetchDocs(
                query(
                  collection(db, "users"),
                  where("referredBy", "==", agentData.uid || agentData.id)
                )
              );
              agentData.referralCount = referralsSnap.size || 0;
            } catch (rErr) {
              agentData.referralCount = 0;
            }
            return agentData;
          })
        );
        setAgents(agentsList);

        setRecentJobs(recentJobsList);
        setTopCompanies(topCompaniesList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleTeamFormChange = (e) => {
    const { name, value } = e.target;
    setTeamForm({ ...teamForm, [name]: value });
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    setTeamFormLoading(true);
    setTeamFormError("");

    try {
      // Create Firebase Auth user for the team member using secondary auth instance
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        teamForm.email,
        teamForm.password
      );
      const uid = userCredential.user.uid;

      // Create user document in Firestore
      await setDoc(doc(db, "users", uid), {
        firstName: teamForm.firstName,
        lastName: teamForm.lastName,
        email: teamForm.email,
        phone: teamForm.phone,
        address: teamForm.address,
        userType: "team",
        uid: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Sign out the team member from secondary auth (doesn't affect main auth)
      await secondaryAuth.signOut();

      // Refresh team members list
      const teamSnap = await getDocs(
        query(collection(db, "users"), where("userType", "==", "team"))
      );
      const teamList = teamSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeamMembers(teamList);

      // Store credentials for display
      setNewTeamCredentials({
        email: teamForm.email,
        password: teamForm.password,
        name: `${teamForm.firstName} ${teamForm.lastName}`,
      });

      // Reset form and close modal
      setTeamForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        password: "Team@1234",
      });
      setShowTeamModal(false);
      setShowTeamCredentialsModal(true);
    } catch (error) {
      console.error("Error adding team member:", error);
      setTeamFormError(error.message || "Failed to add team member");
    } finally {
      setTeamFormLoading(false);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    setTeamFormLoading(true);
    setTeamFormError("");

    try {
      const teamRef = doc(db, "users", editingTeamId);
      await updateDoc(teamRef, {
        firstName: teamForm.firstName,
        lastName: teamForm.lastName,
        phone: teamForm.phone,
        address: teamForm.address,
        updatedAt: serverTimestamp(),
      });

      // Refresh team members list
      const teamSnap = await getDocs(
        query(collection(db, "users"), where("userType", "==", "team"))
      );
      const teamList = teamSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeamMembers(teamList);

      // Reset form and close modal
      setTeamForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        password: "Team@1234",
      });
      setEditingTeamId(null);
      setShowTeamModal(false);
    } catch (error) {
      console.error("Error updating team member:", error);
      setTeamFormError(error.message || "Failed to update team member");
    } finally {
      setTeamFormLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (window.confirm("Are you sure you want to delete this team member?")) {
      try {
        const teamRef = doc(db, "users", teamId);
        await deleteDoc(teamRef);

        // Refresh team members list
        const teamSnap = await getDocs(
          query(collection(db, "users"), where("userType", "==", "team"))
        );
        const teamList = teamSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTeamMembers(teamList);
      } catch (error) {
        console.error("Error deleting team member:", error);
        alert("Failed to delete team member: " + error.message);
      }
    }
  };

  const handleEditTeam = (team) => {
    setTeamForm({
      firstName: team.firstName || "",
      lastName: team.lastName || "",
      email: team.email || "",
      phone: team.phone || "",
      address: team.address || "",
      password: "Team@1234",
    });
    setEditingTeamId(team.id);
    setShowTeamListModal(false); // Close the team list modal
    setShowTeamModal(true);
  };

  const handleAgentFormChange = (e) => {
    const { name, value } = e.target;
    setAgentForm({ ...agentForm, [name]: value });
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    setAgentFormLoading(true);
    setAgentFormError("");

    try {
      // Create Firebase Auth user for the agent using secondary auth instance
      // This prevents logging out the current super admin
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        agentForm.email,
        agentForm.password
      );
      const uid = userCredential.user.uid;

      // Create user document in Firestore
      await setDoc(doc(db, "users", uid), {
        firstName: agentForm.firstName,
        lastName: agentForm.lastName,
        email: agentForm.email,
        phone: agentForm.phone,
        address: agentForm.address,
        userType: "agent",
        commissionTiers: agentForm.commissionTiers,
        uid: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Sign out the agent from secondary auth (doesn't affect main auth)
      await secondaryAuth.signOut();

      // Refresh agents list
      const agentsSnap = await getDocs(
        query(collection(db, "users"), where("userType", "==", "agent"))
      );
      const agentsList = agentsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgents(agentsList);

      // Store credentials for display
      setNewAgentCredentials({
        email: agentForm.email,
        password: agentForm.password,
        name: `${agentForm.firstName} ${agentForm.lastName}`,
      });

      // Reset form and close modal
      setAgentForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        password: "Agent@123",
        commissionTiers: [{ minAmount: 0, maxAmount: 5000, percentage: 10 }],
      });
      setShowAgentModal(false);
      setShowCredentialsModal(true);
    } catch (error) {
      console.error("Error adding agent:", error);
      setAgentFormError(error.message || "Failed to add agent");
    } finally {
      setAgentFormLoading(false);
    }
  };

  const handleViewAgentReferrals = async (agent) => {
    // Navigate to agent details page
    navigate(`/superadmin/agents/${agent.id}`);
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete agent "${agentName}"?\n\nThis will permanently delete:\n- Agent profile from database\n- Agent login credentials\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingAgent(agentId);

    try {
      const result = await deleteUser(agentId);

      if (result.success) {
        // Remove agent from local state
        setAgents((prev) => prev.filter((a) => a.id !== agentId));
        alert("Agent deleted successfully!");
      } else {
        alert(`Failed to delete agent: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting agent:", error);
      alert("An error occurred while deleting the agent. Please try again.");
    } finally {
      setDeletingAgent(null);
    }
  };

  const adminCards = [
    {
      title: "Manage Users",
      icon: "👥",
      path: isTeamView ? "/team/users" : "/superadmin/users",
      count: stats.users,
      description: "Manage all users and permissions",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      badgeColor: "bg-blue-100 text-blue-700",
      hoverColor: "hover:border-blue-200",
    },
    {
      title: "Companies",
      icon: "🏢",
      path: isTeamView ? "/team/companies" : "/superadmin/companies",
      count: stats.companies,
      description: "Review and verify companies",
      bgColor: "bg-purple-50",
      textColor: "text-purple-600",
      badgeColor: "bg-purple-100 text-purple-700",
      hoverColor: "hover:border-purple-200",
    },
    {
      title: "Jobs",
      icon: "📋",
      path: isTeamView ? "/team/jobs" : "/superadmin/jobs",
      count: stats.jobs,
      description: "Monitor and manage job postings",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      badgeColor: "bg-green-100 text-green-700",
      hoverColor: "hover:border-green-200",
    },
    ...(isTeamView
      ? []
      : [
        {
          title: "Packages",
          icon: "📦",
          path: "/superadmin/packages",
          count: stats.packages,
          description: "Create and manage subscription packages",
          bgColor: "bg-orange-50",
          textColor: "text-orange-600",
          badgeColor: "bg-orange-100 text-orange-700",
          hoverColor: "hover:border-orange-200",
        },
        {
          title: "Subscriptions",
          icon: "💳",
          path: "#",
          count: stats.subscriptions,
          description: "View all package subscriptions",
          bgColor: "bg-indigo-50",
          textColor: "text-indigo-600",
          badgeColor: "bg-indigo-100 text-indigo-700",
          hoverColor: "hover:border-indigo-200",
          onClick: handleViewPaymentDetails,
        },
        {
          title: "Revenue",
          icon: "💰",
          path: "#",
          count: `₹${stats.revenue.toLocaleString()}`,
          description: "Total revenue from packages",
          bgColor: "bg-emerald-50",
          textColor: "text-emerald-600",
          badgeColor: "bg-emerald-100 text-emerald-700",
          hoverColor: "hover:border-emerald-200",
          onClick: handleViewPaymentDetails,
        },
        {
          title: "Agents/Franchise",
          icon: "🤝",
          path: "#",
          count: agents.length,
          description: "Manage agents and their referrals",
          bgColor: "bg-teal-50",
          textColor: "text-teal-600",
          badgeColor: "bg-teal-100 text-teal-700",
          hoverColor: "hover:border-teal-200",
          onClick: () => setShowAgentListModal(true),
        },
        {
          title: "Package Approvals",
          icon: "✅",
          path: "/superadmin/approvals",
          count: "Action",
          description: "Approve seeker/employer pages",
          bgColor: "bg-rose-50",
          textColor: "text-rose-600",
          badgeColor: "bg-rose-100 text-rose-700",
          hoverColor: "hover:border-rose-200",
        },
      ]),
    ...(isTeamView
      ? []
      : [
        {
          title: "Team Members",
          icon: "👔",
          path: "#",
          count: teamMembers.length,
          description: "Manage team members",
          bgColor: "bg-cyan-50",
          textColor: "text-cyan-600",
          badgeColor: "bg-cyan-100 text-cyan-700",
          hoverColor: "hover:border-cyan-200",
          onClick: () => setShowTeamListModal(true),
        },
        {
        title: 'Franchises',
        icon: '🏪',
        path: '/superadmin/franchises',
        count: stats.franchises,
        description: 'Manage franchise partners',
        bgColor: 'bg-accent-50',
        textColor: 'text-accent-600',
        badgeColor: 'bg-accent-100 text-accent-700',
        hoverColor: 'hover:border-accent-200'
      }
      ]),
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isTeamView ? "Team Dashboard" : "Super Admin Dashboard"}
          </h1>
          <p className="text-gray-600">
            {isTeamView
              ? "Manage and monitor platform data"
              : "Manage and monitor the job portal platform"}
          </p>
        </div>
        {!isTeamView && (
          <button
            onClick={() => setShowActivateEmailModal(true)}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-5 py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2"
          >
            ⚡ Activate Package by Email
          </button>
        )}
      </div>

      {/* Main Stats Cards */}
      <div
        className={`grid grid-cols-1 ${isTeamView ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"
          } gap-6 mb-8`}
      >
        <div
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          onClick={() =>
            navigate(isTeamView ? "/team/users" : "/superadmin/users")
          }
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Users</p>
              <p className="text-3xl font-bold">{stats.users}</p>
              <p className="text-blue-100 text-sm mt-1">Registered users</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          onClick={() =>
            navigate(isTeamView ? "/team/companies" : "/superadmin/companies")
          }
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Companies</p>
              <p className="text-3xl font-bold">{stats.companies}</p>
              <p className="text-purple-100 text-sm mt-1">
                {stats.pendingCompanies} pending verification
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          onClick={() =>
            navigate(isTeamView ? "/team/jobs" : "/superadmin/jobs", {
              state: { filterStatus: 'active' }
            })
          }
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Active Jobs</p>
              <p className="text-3xl font-bold">{stats.activeJobs}</p>
              <p className="text-green-100 text-sm mt-1">
                {stats.jobs} total jobs
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M17 20H7m10 0v-2a3 3 0 00-5.356-1.857M17 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {!isTeamView && (
          <div
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            onClick={handleViewPaymentDetails}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">
                  Total Revenue
                </p>
                <p className="text-3xl font-bold">
                  ₹{stats.revenue.toLocaleString()}
                </p>
                <p className="text-emerald-100 text-sm mt-1">
                  ₹{stats.monthlyRevenue.toLocaleString()} this month
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {adminCards.map((card, index) => (
          <div
            key={index}
            onClick={() =>
              card.onClick ? card.onClick() : navigate(card.path)
            }
            className={`${card.bgColor} rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all duration-300 border border-gray-200 ${card.hoverColor} transform hover:-translate-y-1`}
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-2xl shadow-sm">
                {card.icon}
              </div>
              <div className="flex-1">
                <h3 className={`${card.textColor} font-bold text-lg mb-1`}>
                  {card.title}
                </h3>
                <p className="text-gray-600 text-sm mb-2">{card.description}</p>
                {card.count !== undefined && (
                  <div
                    className={`${card.badgeColor} px-3 py-1 rounded-full text-sm font-semibold inline-block`}
                  >
                    {card.title === "Revenue"
                      ? card.count
                      : `Total: ${card.count}`}
                  </div>
                )}
              </div>
              <div className="text-gray-400">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity Grid */}
      {!isTeamView && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Jobs */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 20H7m10 0v-2a3 3 0 00-5.356-1.857M16 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Recent Jobs</h3>
              </div>
              <button
                onClick={() => navigate("/superadmin/jobs")}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View All →
              </button>
            </div>

            {recentJobs.length > 0 ? (
              <div className="space-y-4">
                {recentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {job.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-2">
                          {job.companyDetails?.name ||
                            job.company ||
                            "Unknown Company"}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>
                            📍{" "}
                            {typeof job.location === "object"
                              ? `${job.location.city || ""}, ${job.location.country || ""
                                }`.replace(/^,\s*/, "")
                              : job.location || "Not specified"}
                          </span>
                          <span>
                            👥 {job.applicationCount || 0} applications
                          </span>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${job.status === "active"
                          ? "bg-green-100 text-green-700"
                          : job.status === "closed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {job.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-3">💼</div>
                <p className="text-gray-500">No recent jobs</p>
              </div>
            )}
          </div>

          {/* Top Companies */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Top Companies
                </h3>
              </div>
              <button
                onClick={() => navigate("/superadmin/companies")}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                View All →
              </button>
            </div>

            {topCompanies.length > 0 ? (
              <div className="space-y-4">
                {topCompanies.map((company, index) => (
                  <div
                    key={company.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      #{index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {company.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {company.industry || "Industry not specified"}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-purple-600">
                        {company.jobCount}
                      </div>
                      <div className="text-xs text-gray-500">jobs posted</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-3">🏢</div>
                <p className="text-gray-500">No companies yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Agent Modal */}
      {showAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Add New Agent <span className="text-gray-500">/ नवीन एजंट जोडा</span>
              </h2>
              <button
                onClick={() => setShowAgentModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddAgent} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <BilingualLabel english="First Name" marathi="पहिले नाव" required className="mb-2" />
                  <input
                    type="text"
                    name="firstName"
                    value={agentForm.firstName}
                    onChange={handleAgentFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter first name / पहिले नाव प्रविष्ट करा"
                  />
                </div>
                <div>
                  <BilingualLabel english="Last Name" marathi="आडनाव" required className="mb-2" />
                  <input
                    type="text"
                    name="lastName"
                    value={agentForm.lastName}
                    onChange={handleAgentFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter last name / आडनाव प्रविष्ट करा"
                  />
                </div>
              </div>

              <div>
                <BilingualLabel english="Email" marathi="ईमेल" required className="mb-2" />
                <input
                  type="email"
                  name="email"
                  value={agentForm.email}
                  onChange={handleAgentFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter email address / ईमेल पत्ता प्रविष्ट करा"
                />
              </div>

              <div>
                <BilingualLabel english="Phone" marathi="फोन" required className="mb-2" />
                <input
                  type="tel"
                  name="phone"
                  value={agentForm.phone}
                  onChange={handleAgentFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter phone number / फोन नंबर प्रविष्ट करा"
                />
              </div>

              <div>
                <BilingualLabel english="Address" marathi="पत्ता" required className="mb-2" />
                <textarea
                  name="address"
                  value={agentForm.address}
                  onChange={handleAgentFormChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter address / पत्ता प्रविष्ट करा"
                />
              </div>

              <div>
                <BilingualLabel english="Password" marathi="पासवर्ड" required className="mb-2" />
                <input
                  type="text"
                  name="password"
                  value={agentForm.password}
                  onChange={handleAgentFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter password / पासवर्ड प्रविष्ट करा"
                />
                <p className="mt-1 text-sm text-gray-500">Default: Agent@123</p>
              </div>

              {/* Commission Tiers Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">
                      Commission Tiers <span className="text-gray-500">/ कमिशन स्तर</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Define commission percentages based on amount ranges <span className="text-gray-500">/ रक्कम श्रेणीवर आधारित कमिशन टक्केवारी परिभाषित करा</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAgentForm((prev) => ({
                        ...prev,
                        commissionTiers: [
                          ...prev.commissionTiers,
                          {
                            minAmount:
                              prev.commissionTiers[
                                prev.commissionTiers.length - 1
                              ]?.maxAmount || 0,
                            maxAmount:
                              (prev.commissionTiers[
                                prev.commissionTiers.length - 1
                              ]?.maxAmount || 0) + 5000,
                            percentage: 10,
                          },
                        ],
                      }));
                    }}
                    className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg transition-colors text-sm flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Tier
                  </button>
                </div>

                <div
                  className="space-y-3 max-h-80 overflow-y-auto pr-2"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#d1d5db transparent",
                  }}
                >
                  {agentForm.commissionTiers.map((tier, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-700">
                          Tier {index + 1}
                        </span>
                        {agentForm.commissionTiers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setAgentForm((prev) => ({
                                ...prev,
                                commissionTiers: prev.commissionTiers.filter(
                                  (_, i) => i !== index
                                ),
                              }));
                            }}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Min Amount (₹) <span className="text-gray-500">/ किमान रक्कम</span>
                          </label>
                          <input
                            type="number"
                            value={tier.minAmount}
                            onChange={(e) => {
                              const newTiers = [...agentForm.commissionTiers];
                              newTiers[index].minAmount =
                                parseFloat(e.target.value) || 0;
                              setAgentForm((prev) => ({
                                ...prev,
                                commissionTiers: newTiers,
                              }));
                            }}
                            required
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Max Amount (₹) <span className="text-gray-500">/ कमाल रक्कम</span>
                          </label>
                          <input
                            type="number"
                            value={tier.maxAmount}
                            onChange={(e) => {
                              const newTiers = [...agentForm.commissionTiers];
                              newTiers[index].maxAmount =
                                parseFloat(e.target.value) || 0;
                              setAgentForm((prev) => ({
                                ...prev,
                                commissionTiers: newTiers,
                              }));
                            }}
                            required
                            min={tier.minAmount}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                            placeholder="5000"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Commission (%) <span className="text-gray-500">/ कमिशन</span>
                          </label>
                          <input
                            type="number"
                            value={tier.percentage}
                            onChange={(e) => {
                              const newTiers = [...agentForm.commissionTiers];
                              newTiers[index].percentage =
                                parseFloat(e.target.value) || 0;
                              setAgentForm((prev) => ({
                                ...prev,
                                commissionTiers: newTiers,
                              }));
                            }}
                            required
                            min="0"
                            max="100"
                            step="0.1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                            placeholder="10"
                          />
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600 bg-white rounded px-2 py-1">
                        ₹{tier.minAmount.toLocaleString()} - ₹
                        {tier.maxAmount.toLocaleString()} = {tier.percentage}%
                        commission
                      </div>
                    </div>
                  ))}
                </div>

                {/* Example Display */}
                {/* <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="text-xs text-indigo-700">
                      <p className="font-semibold mb-1">Example:</p>
                      <p>If payment is ₹12,000 and tiers are:</p>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li>₹0-₹5,000 = 10% → ₹500</li>
                        <li>₹5,000-₹10,000 = 15% → ₹750</li>
                        <li>₹10,000-₹20,000 = 20% → ₹400 (for ₹2,000)</li>
                        <li>
                          <strong>Total Commission: ₹1,650</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div> */}
              </div>

              {agentFormError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-red-700 text-sm font-medium">
                    {agentFormError}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAgentModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={agentFormLoading}
                  className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {agentFormLoading ? "Adding..." : "Add Agent"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agents List Modal */}
      {showAgentListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">All Agents</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAgentListModal(false);
                    setShowAgentModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Agent
                </button>
                <button
                  onClick={() => setShowAgentListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {agents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Phone
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Commission Tiers
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Joined
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent, index) => (
                      <tr
                        key={agent.id}
                        className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          }`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                              {agent.firstName?.charAt(0)?.toUpperCase() || "A"}
                            </div>
                            <p className="font-medium text-gray-900">
                              {agent.firstName} {agent.lastName}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.email}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.phone}
                        </td>
                        <td className="py-4 px-4">
                          {agent.commissionTiers &&
                            agent.commissionTiers.length > 0 ? (
                            <div className="space-y-1">
                              {agent.commissionTiers.map((tier, idx) => (
                                <div
                                  key={idx}
                                  className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded"
                                >
                                  ₹{tier.minAmount.toLocaleString()}-₹
                                  {tier.maxAmount.toLocaleString()}:{" "}
                                  {tier.percentage}%
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">
                              No tiers set
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.createdAt?.toDate?.()?.toLocaleDateString() ||
                            "N/A"}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewAgentReferrals(agent)}
                              className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg transition-colors text-sm flex items-center gap-2"
                              title="View Referrals"
                            >
                              <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-full text-xs font-bold">
                                {agent.referralCount || 0}
                              </span>
                              Referrals
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteAgent(
                                  agent.id,
                                  `${agent.firstName} ${agent.lastName}`
                                )
                              }
                              disabled={deletingAgent === agent.id}
                              className={`p-2 rounded-lg transition-colors ${deletingAgent === agent.id
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-red-100 hover:bg-red-200 text-red-600"
                                }`}
                              title="Delete Agent"
                            >
                              {deletingAgent === agent.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                              ) : (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🤝</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No agents yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Add your first agent to start tracking referrals.
                </p>
                <button
                  onClick={() => setShowAgentModal(true)}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add First Agent
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agent Credentials Success Modal */}
      {showCredentialsModal && newAgentCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Agent Created Successfully!
              </h2>
              <p className="text-gray-600">
                Share these credentials with {newAgentCredentials.name}
              </p>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-200">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email (Login ID)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newAgentCredentials.email}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          newAgentCredentials.email
                        );
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy email"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newAgentCredentials.password}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          newAgentCredentials.password
                        );
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy password"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Important!
                  </p>
                  <p className="text-sm text-yellow-700">
                    Make sure to save these credentials. The agent will use them
                    to login at the login page.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowCredentialsModal(false);
                setNewAgentCredentials(null);
              }}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
            >
              Got it, Close
            </button>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentDetails && (() => {
        const filteredPayments = getFilteredPayments();
        const filteredRevenue = filteredPayments.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-6 max-h-[92vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">💳 Payment Details</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Showing <span className="font-semibold text-emerald-600">{filteredPayments.length}</span> of {paymentDetails.length} transactions
                    &nbsp;·&nbsp; Total: <span className="font-bold text-emerald-700">₹{filteredRevenue.toLocaleString()}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
                    title="Export filtered data to CSV"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    CSV
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
                    title="Export filtered data to Excel"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Excel
                  </button>
                  <button
                    onClick={() => setShowPaymentDetails(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 ml-1"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* ── FILTER BAR ── */}
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 border border-gray-200 rounded-2xl p-4 mb-5 space-y-4">
                {/* Row 1: Date Range Quick Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide mr-1">📅 Date Range:</span>
                  {[
                    { key: 'all', label: 'All Time', bg: 'bg-gray-600', ring: 'ring-gray-400' },
                    { key: 'today', label: 'Today', bg: 'bg-sky-500', ring: 'ring-sky-300' },
                    { key: 'last7', label: 'Last 7 Days', bg: 'bg-violet-500', ring: 'ring-violet-300' },
                    { key: 'lastMonth', label: 'Last Month', bg: 'bg-orange-500', ring: 'ring-orange-300' },
                    { key: 'custom', label: '📆 Custom', bg: 'bg-rose-500', ring: 'ring-rose-300' },
                  ].map(btn => (
                    <button
                      key={btn.key}
                      onClick={() => setPaymentFilterRange(btn.key)}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold text-white transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 ${paymentFilterRange === btn.key
                          ? `${btn.bg} ring-2 ${btn.ring} scale-105 shadow-md`
                          : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                        }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>

                {/* Custom Date Inputs — shown only when 'custom' is selected */}
                {paymentFilterRange === 'custom' && (
                  <div className="flex flex-wrap items-center gap-3 pl-1">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-gray-600">From:</label>
                      <input
                        type="date"
                        value={paymentFilterCustomStart}
                        onChange={e => setPaymentFilterCustomStart(e.target.value)}
                        className="px-3 py-1.5 border border-rose-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-gray-600">To:</label>
                      <input
                        type="date"
                        value={paymentFilterCustomEnd}
                        onChange={e => setPaymentFilterCustomEnd(e.target.value)}
                        className="px-3 py-1.5 border border-rose-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* Row 2: User Type + Search */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* User Type Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">👤 Type:</span>
                    {[
                      { key: 'all', label: 'All', bg: 'bg-gray-600' },
                      { key: 'jobseeker', label: 'Job Seeker', bg: 'bg-blue-500' },
                      { key: 'employer', label: 'Company', bg: 'bg-purple-500' },
                    ].map(btn => (
                      <button
                        key={btn.key}
                        onClick={() => setPaymentFilterUserType(btn.key)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold text-white transition-all duration-200 ${paymentFilterUserType === btn.key
                            ? `${btn.bg} ring-2 ring-offset-1 ring-current scale-105 shadow`
                            : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                          }`}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>

                  {/* Search */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by name, email or package..."
                        value={paymentSearchQuery}
                        onChange={e => setPaymentSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                      />
                    </div>
                  </div>

                  {/* Clear Filters */}
                  {(paymentFilterRange !== 'all' || paymentFilterUserType !== 'all' || paymentSearchQuery) && (
                    <button
                      onClick={() => {
                        setPaymentFilterRange('all');
                        setPaymentFilterUserType('all');
                        setPaymentFilterCustomStart('');
                        setPaymentFilterCustomEnd('');
                        setPaymentSearchQuery('');
                      }}
                      className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-full text-xs font-semibold transition-colors"
                    >
                      ✕ Clear Filters
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-emerald-50 to-teal-50">
                    <tr className="border-b-2 border-emerald-200">
                      <th className="text-left py-4 px-4 font-bold text-gray-700">User</th>
                      <th className="text-left py-4 px-4 font-bold text-gray-700">Email</th>
                      <th className="text-left py-4 px-4 font-bold text-gray-700">Mobile No</th>
                      <th className="text-left py-4 px-4 font-bold text-gray-700">Type</th>
                      <th className="text-left py-4 px-4 font-bold text-gray-700">Amount</th>
                      <th className="text-left py-4 px-4 font-bold text-gray-700">Package</th>
                      <th className="text-left py-4 px-4 font-bold text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-16 text-center">
                          <div className="text-gray-400 text-5xl mb-3">🔍</div>
                          <p className="text-gray-500 font-medium">No payments found for selected filters</p>
                          <p className="text-gray-400 text-sm mt-1">Try changing the date range or clearing filters</p>
                        </td>
                      </tr>
                    ) : (
                      filteredPayments.map((payment, index) => (
                        <tr
                          key={payment.id}
                          onClick={() => {
                            if (payment.userId) {
                              navigate(`/admin/user/${payment.userId}`);
                            } else {
                              alert("User ID not found for this transaction.");
                            }
                          }}
                          className={`border-b border-gray-100 hover:bg-emerald-50 cursor-pointer transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }`}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center">
                              <div className="w-9 h-9 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold mr-3 text-sm">
                                {payment.userName?.charAt(0)?.toUpperCase() || "U"}
                              </div>
                              <span className="font-medium text-gray-900">{payment.userName || "Unknown"}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">{payment.userEmail || "No email"}</td>
                          <td className="py-4 px-4 text-gray-600 text-sm">{payment.userPhone || "N/A"}</td>
                          <td className="py-4 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${payment.userType === "jobseeker"
                                ? "bg-blue-100 text-blue-700"
                                : payment.userType === "employer"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}>
                              {payment.userType === "jobseeker" ? "👤 Job Seeker" : payment.userType === "employer" ? "🏢 Company" : "❓ Unknown"}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-bold text-emerald-600 text-lg">₹{payment.totalAmount?.toLocaleString() || "0"}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium">{payment.packageName || "Unknown"}</span>
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">{payment.date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) || "Unknown"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Team Member Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTeamId ? "Edit Team Member" : "Add New Team Member"}
              </h2>
              <button
                onClick={() => {
                  setShowTeamModal(false);
                  setEditingTeamId(null);
                  setTeamForm({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    address: "",
                    password: "Team@1234",
                  });
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form
              onSubmit={editingTeamId ? handleUpdateTeam : handleAddTeam}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={teamForm.firstName}
                    onChange={handleTeamFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={teamForm.lastName}
                    onChange={handleTeamFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={teamForm.email}
                  onChange={handleTeamFormChange}
                  required
                  disabled={editingTeamId}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={teamForm.phone}
                  onChange={handleTeamFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="address"
                  value={teamForm.address}
                  onChange={handleTeamFormChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter address"
                />
              </div>

              {!editingTeamId && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="text"
                    name="password"
                    value={teamForm.password}
                    onChange={handleTeamFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter password"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Default: Team@1234
                  </p>
                </div>
              )}

              {teamFormError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-red-700 text-sm font-medium">
                    {teamFormError}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTeamModal(false);
                    setEditingTeamId(null);
                    setTeamForm({
                      firstName: "",
                      lastName: "",
                      email: "",
                      phone: "",
                      address: "",
                      password: "Team@1234",
                    });
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={teamFormLoading}
                  className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {teamFormLoading
                    ? "Processing..."
                    : editingTeamId
                      ? "Update Member"
                      : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Members List Modal */}
      {showTeamListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowTeamListModal(false);
                    setShowTeamModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Team Member
                </button>
                <button
                  onClick={() => setShowTeamListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {teamMembers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Phone
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Address
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Joined
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map((team, index) => (
                      <tr
                        key={team.id}
                        onClick={() => handleEditTeam(team)}
                        className={`border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors ${index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          }`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                              {team.firstName?.charAt(0)?.toUpperCase() || "T"}
                            </div>
                            <p className="font-medium text-gray-900">
                              {team.firstName} {team.lastName}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.email}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.phone}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.address}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.createdAt?.toDate?.()?.toLocaleDateString() ||
                            "N/A"}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTeam(team);
                              }}
                              className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold rounded-lg transition-colors text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTeam(team.id);
                              }}
                              className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-lg transition-colors text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">👔</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No team members yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Add your first team member to get started.
                </p>
                <button
                  onClick={() => {
                    setShowTeamListModal(false);
                    setShowTeamModal(true);
                  }}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add First Team Member
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Team Credentials Success Modal */}
      {showTeamCredentialsModal && newTeamCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Team Member Created Successfully!
              </h2>
              <p className="text-gray-600">
                Share these credentials with {newTeamCredentials.name}
              </p>
            </div>

            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 mb-6 border border-cyan-200">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email (Login ID)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTeamCredentials.email}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(newTeamCredentials.email);
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy email"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTeamCredentials.password}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          newTeamCredentials.password
                        );
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy password"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Important!
                  </p>
                  <p className="text-sm text-yellow-700">
                    Make sure to save these credentials. The team member will
                    use them to login at the login page.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowTeamCredentialsModal(false);
                setNewTeamCredentials(null);
              }}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
            >
              Got it, Close
            </button>
          </div>
        </div>
      )}

      {/* Activate Package by Email Modal */}
      <ActivatePackageByEmailModal
        isOpen={showActivateEmailModal}
        onClose={() => setShowActivateEmailModal(false)}
      />
    </div>
  );
};

export default SuperAdminDashboard;
